import { createSignal, onMount, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { addShowpadCode } from '../../tools/add-showpad-code';
import { Button } from '../../ui-components/button/button';
import clsx from 'clsx';
import { errorDescribedBy, fieldErrorId, fieldShellClasses, FormFieldLabel } from '../forms-tw/forms-fields/primitives';
import { FlexRow } from '../../ui-components/utility-style-components/flex';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { Modal } from '../../ui-components/modal/modal';
import { VerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { Text } from '../../ui-components/text/text';
import { TabHandler } from '../../tools/tab-handler';
import { ShowpadCodeSuccessModal } from '../showpad-code-success-modal/showpad-code-success-modal';
import { ErrorCatcher } from '../../tools/error-catcher';
import { ArrowDownIcon } from '../icons-library/arrow-down';
import { ArrowUpIcon } from '../icons-library/arrow-up';
import theme from '../../style/theme';

export const ShowpadCodeSubmission = () => {
    const [errorMessage, setErrorMessage] = createSignal('');
    const [showModal, setShowModal] = createSignal(false);
    const [code, setCode] = createSignal('');
    const [submitting, setSubmitting] = createSignal(false);
    const [expanded, setExpanded] = createSignal(false);
    const [showSuccessModal, setShowSuccessModal] = createSignal(false);
    const [emailTriggered, setEmailTriggered] = createSignal(false);

    const { 
        localize,
        userState,
        updateUser,
        contactUsPage,
        viewport,
    } = useContext(AppContext);

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    onMount(() => {
        window.addEventListener('keydown', keydownListener);
    });

    const closeModal = () => {
        setShowModal(false);
    };

    const keydownListener = (e: any) => {
        if (showModal() && e.key === 'Escape') {
            closeModal();
        }
    };

    const handleInputChange = (value: string) => {
        setErrorMessage('');
        setCode(value);
    };

    const submitCode = async () => {
        setSubmitting(true);
        setErrorMessage('');

        const result = await addShowpadCode({ showpadCode: code() }, updateUser);
        setSubmitting(false);

        if (!result.success) {
            if (result.message === 'invalid-showpad-code') {
                setErrorMessage(localize('invalid-showpad-code', 'The E-Learning access code you entered is invalid'));
                setShowModal(true);
            } else {
                console.error('Error:', result.message);
                setErrorMessage(localize('error-has-occurred', 'An error has occurred, please try again later'));
            }

            return;
        }

        setCode('');
        setEmailTriggered(result.emailTriggered === true);
        setShowSuccessModal(true);
    };

    const HelpModal = () => (

        <TabHandler refs={{
            firstTabbableElement,
            lastTabbableElement,
        }}>
            <Modal
                visible={true}
                onClose={() => closeModal()}
                size='small'
                darkenBackground={true}
                closeButtonRef={(el: HTMLElement) => setFirstTabbableElement(el)}
            >
                <VerticalSpace size={5} />
                <Text fontSize='large' fontStyle='bold'>
                    {localize('no-code-modal-header', 'Get access to E-Learning courses')}
                </Text>

                <Text fontSize='normal' splitParagraph={true}>
                    {localize('no-code-modal-text', 'Please contact your local administrator to get an E-Learning access code to access the E-Learning courses.')}
                </Text>

                <FlexRow gap='2rem' justifyContent='center' alignItems='center'>
                    <Button
                        label={localize('close-modal-button-text', 'Close')}
                        variant='secondary'
                        onClick={closeModal}
                        noBlockSpacing
                    />

                    <Button
                        label={localize('contact-us', 'Contact us')}
                        isLink={true}
                        url={contactUsPage.url}
                        element={(el: HTMLElement) => {
                            setLastTabbableElement(el);
                        }}
                        noBlockSpacing
                    />
                </FlexRow>

            </Modal>
        </TabHandler>
    );

    return (

        <ErrorCatcher componentName='ShowpadCodeSubmission' logErrorToServer={true}>
            <Show when={userState.user}>
                <div class="bg-cvi-bg-brand-card rounded-cvi-xl px-cvi-6 py-cvi-8 max-mobile:px-cvi-5 max-mobile:py-cvi-7">
                    <button
                        type="button"
                        class="flex flex-col flex-row desktop:items-center justify-between gap-cvi-5 w-full cursor-pointer bg-transparent border-0 p-0 text-left"
                        onClick={() => setExpanded(!expanded())}
                        aria-expanded={expanded()}
                    >
                        <div class="flex flex-col desktop:flex-row desktop:items-center gap-cvi-5">
                            <span class="body-md font-cvi-regular text-cvi-fg-default">
                                {`${localize('your-current-showpad-code', 'Your current access code')}:`}
                            </span>
                            <span class="self-start inline-flex items-center px-cvi-4 py-cvi-1 rounded-cvi-md border border-cvi-border-default bg-cvi-bg-body body-sm font-cvi-medium text-cvi-fg-default">
                                {userState?.user?.showpadCode || localize('no-code-set', 'No code set')}
                            </span>
                        </div>

                        <Show when={!expanded()}>
                            <span class="flex items-center self-end gap-cvi-3 body-md font-cvi-medium text-cvi-fg-link">
                                {!userState?.user?.showpadCode
                                    ? localize('add-code', 'Add code')
                                    : localize('change-code', 'Change code')}
                                <ArrowDownIcon width={1.82} height={1.82} fill="currentColor" ariaHidden />
                            </span>
                        </Show>
                        <Show when={expanded() && viewport.width > theme.breakpoints.DESKTOP}>
                            <span class="flex items-center gap-cvi-3 body-md font-cvi-medium text-cvi-fg-link">
                                <ArrowUpIcon width={1.82} height={1.82} fill="currentColor" ariaHidden />
                            </span>
                        </Show>
                    </button>

                    <div class="grid transition-all duration-200 ease-out"
                        style={{
                            'grid-template-rows': expanded() ? '1fr' : '0fr',
                            'opacity': expanded() ? 1 : 0,
                        }}
                    >
                        <div class="overflow-hidden">
                            <div class="flex gap-4 my-4 flex-col px-2">
                                <VerticalSpace size={1} />

                                <div class="max-w-[35rem]">
                                    <FormFieldLabel for="showpad-code-input">
                                        {localize('course-access-code', 'E-Learning Access code')}
                                    </FormFieldLabel>
                                    {/* Monospace keeps look-alike characters in codes (O/0, l/1/I) apart. */}
                                    <input
                                        id="showpad-code-input"
                                        value={code()}
                                        onInput={(e) => handleInputChange(e.currentTarget.value)}
                                        placeholder={localize('enter-code-placeholder', 'Enter new access code')}
                                        name='showpad-code'
                                        aria-invalid={!!errorMessage() || undefined}
                                        aria-describedby={errorDescribedBy('showpad-code-input', !!errorMessage())}
                                        class={clsx(fieldShellClasses(!!errorMessage()), 'font-mono')}
                                    />
                                </div>

                                <Button
                                    label={localize('submit-code', 'Submit Access code')}
                                    onClick={() => submitCode()}
                                    noBlockSpacing
                                    disabled={submitting() || !code()}
                                />

                                <Show when={submitting()}>
                                    <LoadingSpinner size='2.2rem' />
                                </Show>

                                <Show when={errorMessage()}>
                                    <div id={fieldErrorId('showpad-code-input')} class="text-[#d25960] text-[16px] font-atos-regular">{errorMessage()}</div>
                                </Show>

                            </div>

                            <Show when={showModal()}>
                                <HelpModal />
                            </Show>

                            <Show when={showSuccessModal()}>
                                <ShowpadCodeSuccessModal
                                    emailTriggered={emailTriggered()}
                                    onClose={() => setShowSuccessModal(false)}
                                />
                            </Show>
                        </div>
                    </div>
                </div>
            </Show>
        </ErrorCatcher>
    );
};
